import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { z } from 'zod';

import type { SceneGroup } from '@lumen5/luminary';

import type { ToolExecutionContext } from '../types';
import { formatUncoveredRanges, getUncoveredTranscriptRanges } from './overlay-coverage';
import { resolveOverlayWordRangeOperation, setSceneRangeOperation } from './overlay-placement';
import { resolveSceneHandle, sceneDisplayId } from './scene-addressing';
import { partitionTranscriptByScene } from './transcript-partition';
import { findInterleavedSequenceMatch } from './transcript-word-match';
import { VideoStateTool } from './VideoStateTool';

import { SCENE_GROUP_TYPES } from 'js/src/agent/video-state/constants';
import { getSceneGroupType } from 'js/src/agent/video-state/serialization/assetSerializer';
import type { RootState } from 'js/src/libs/RootState';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { isSceneAnOverlay } from 'js/src/luminary/luminary-selectors';

// Slack (ms) so a target that just touches the current coverage isn't rejected over snap jitter.
const ADJACENCY_EPSILON_MILLIS = 1;

// The scene group types set_scene_range applies to: every non-regular type, from the enum.
const VOICE_DRIVEN_SCENE_GROUP_TYPES = Object.values(SCENE_GROUP_TYPES).filter(
    (type) => type !== SCENE_GROUP_TYPES.REGULAR,
);

export const setSceneRangeSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    words: z
        .array(z.string())
        .describe(
            'Consecutive transcript tokens the scene should cover, spelled exactly as they appear (include any `<pause seconds="N"/>` between them).',
        ),
});
export type SetSceneRangeArgs = z.infer<typeof setSceneRangeSchema>;

export class SetSceneRangeTool extends VideoStateTool<typeof setSceneRangeSchema> {
    protected override readonly schema = setSceneRangeSchema;
    // Shrinking an overlay exposes (or extends) a sibling base scene. Non-sticky resizes change
    // nothing else, so show the whole group diff — a scene-scoped diff would hide the base scene the
    // edit just revealed behind a bare sceneCount bump.
    protected override readonly scopesDiffToEditedScene = false;
    protected override readonly toolName = TOOL_NAMES.setSceneRange;
    public override readonly subagentAccess = SubagentAccess.EDITS_TARGET;
    protected override readonly toolDescription =
        'Re-time an existing voiceover scene so it covers exactly the given transcript words — use to grow, shrink, or shift a voiceover scene.';

    public override getToolDisplayName(): string {
        return 'Re-timing scene';
    }

    public get statusIcon(): string {
        return 'straighten';
    }

    public async execute(args: SetSceneRangeArgs, context: ToolExecutionContext) {
        const { words } = args;
        const { parentActionGroup } = context;

        try {
            const state = this.getState();
            const { scene, sceneShortId } = resolveSceneHandle(this.luminary, state, args);

            const sceneGroup = scene.sceneGroup;
            if (!sceneGroup) {
                return `Error: No scene group found for scene ${sceneShortId}`;
            }
            if (getSceneGroupType(sceneGroup) === SCENE_GROUP_TYPES.REGULAR) {
                return `Error: ${TOOL_NAMES.setSceneRange} only re-times scenes on ${VOICE_DRIVEN_SCENE_GROUP_TYPES.join(', ')} scene groups. Scene ${sceneShortId} is on a ${SCENE_GROUP_TYPES.REGULAR} scene group — use ${TOOL_NAMES.setSceneDuration} to change its length.`;
            }
            if (!isSceneAnOverlay(state, scene)) {
                return `Error: Scene ${sceneShortId} is the base/presenter scene of a voice-driven scene group, which carries the voice-over and controls the group's timing, so its range can't be set. Re-time a voiceover scene instead.`;
            }
            if (!words || words.length === 0) {
                return 'Error: words array cannot be empty. Provide the transcript words the scene should cover.';
            }

            const { resolveOverlayWordRange } = makeOperationsCallable(
                { resolveOverlayWordRangeOperation },
                this.luminary,
                this.dispatch,
                this.getState as () => RootState,
            );
            const range = resolveOverlayWordRange(sceneGroup, words);
            if ('error' in range) {
                return `Error: ${range.error}`;
            }
            const { startTimeMillis, endTimeMillis } = range;

            // Must overlap (or touch) the scene's current coverage — this resizes in place, it doesn't
            // teleport. A disjoint target is almost always a mistake; delete + re-add is the way to move.
            const partition = partitionTranscriptByScene(state, sceneGroup);
            const current = partition?.find((entry) => entry.scene.id === scene.id);
            if (current) {
                const disjoint =
                    endTimeMillis < current.startMillis - ADJACENCY_EPSILON_MILLIS ||
                    startTimeMillis > current.endMillis + ADJACENCY_EPSILON_MILLIS;
                if (disjoint) {
                    return `Error: The words "${words.join(' ')}" don't overlap scene ${sceneShortId}'s current coverage, so this would move the scene rather than resize it. To place a scene somewhere unrelated, ${TOOL_NAMES.deleteScene} and then ${TOOL_NAMES.addVoiceoverScene} there instead.`;
                }
            }

            // Refuse a range that fully engulfs another overlay: clearing the target region would
            // delete it outright (with its content). Resizing only pushes against the adjacent scene;
            // removing an overlay is delete_scene's job.
            const engulfed = (partition ?? []).filter(
                (entry) =>
                    entry.role === 'overlay' &&
                    entry.scene.id !== scene.id &&
                    startTimeMillis - ADJACENCY_EPSILON_MILLIS <= entry.startMillis &&
                    entry.endMillis <= endTimeMillis + ADJACENCY_EPSILON_MILLIS,
            );
            if (engulfed.length > 0) {
                const list = engulfed
                    .map(
                        (entry) =>
                            `${sceneDisplayId(this.luminary, entry.scene.id)} ("${entry.words.join(' ')}")`,
                    )
                    .join(', ');
                return `Error: The words "${words.join(' ')}" fully span scene ${list}, so re-timing ${sceneShortId} onto them would delete ${engulfed.length > 1 ? 'those scenes' : 'that scene'} and ${engulfed.length > 1 ? 'their' : 'its'} content. ${TOOL_NAMES.setSceneRange} only resizes against the neighbouring coverage — it won't swallow a whole scene. Extend ${sceneShortId} only as far as the adjacent scene, or ${TOOL_NAMES.deleteScene} the scene(s) you mean to replace first.`;
            }

            const { setSceneRange } = makeOperationsCallable(
                { setSceneRangeOperation },
                this.luminary,
                this.dispatch,
                this.getState as () => RootState,
            );
            let resizeError: string | undefined;
            try {
                setSceneRange(
                    scene.id,
                    sceneGroup.id,
                    startTimeMillis,
                    endTimeMillis,
                    false, // systemTriggered
                    parentActionGroup,
                );
            } catch (error) {
                resizeError = error instanceof Error ? error.message : 'Unknown error occurred';
            }

            return this.describeAchievedCoverage(
                sceneGroup,
                scene.id,
                sceneShortId,
                words,
                resizeError,
            );
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting scene range: ${errorMessage}`;
        }
    }

    /**
     * Report what the resize actually achieved, read back from the video, instead of echoing the
     * requested words. A resize can silently fall short — a degenerate (sub-millisecond) neighbour
     * can't be absorbed by the overlay translation — and reporting the request back as "Success"
     * hides that: the video keeps uncovered narration while lint and the agent both believe the
     * scene was re-timed.
     */
    private describeAchievedCoverage(
        sceneGroup: SceneGroup,
        sceneId: string,
        sceneShortId: string,
        requestedWords: string[],
        resizeError?: string,
    ): string {
        const state = this.getState();
        const entry = partitionTranscriptByScene(state, sceneGroup)?.find(
            (partition) => partition.scene.id === sceneId,
        );
        const achieved = entry?.words ?? [];

        // Compare through the same tolerant matcher that resolved the range, not raw string
        // membership: the request may differ from the stored tokens in case and punctuation, or
        // arrive as one whitespace-joined phrase, and a positional match also handles a repeated
        // word that set membership would treat as already covered.
        const coversRequest =
            entry !== undefined &&
            findInterleavedSequenceMatch(entry.tokens, requestedWords) !== null;

        // A throw is always reported, even when the scene ended up covering the requested words:
        // the operation applies its checkpoint after the resize, so a late failure leaves the edit
        // un-checkpointed.
        if (coversRequest && !resizeError) {
            // Any words a shrink dropped now show the waveform base; we don't call that out here — the
            // appended video diff shows the exposed base scene, and lint's overlay_coverage check names
            // any uncovered words. A note that told the agent to "cover them" only prompted it to undo
            // an intentional shrink.
            return `Success: Scene ${sceneShortId} now covers "${achieved.join(' ')}".`;
        }

        const uncovered = getUncoveredTranscriptRanges(state, sceneGroup) ?? [];
        const reason = resizeError ? ` The resize failed: ${resizeError}.` : '';
        const covering =
            achieved.length > 0
                ? `Scene ${sceneShortId} covers "${achieved.join(' ')}"`
                : `Scene ${sceneShortId} covers no transcript words`;
        const gaps =
            uncovered.length > 0
                ? ` ${formatUncoveredRanges(uncovered, (sceneId) => sceneDisplayId(this.luminary, sceneId))}`
                : '';

        return `Error: ${TOOL_NAMES.setSceneRange} did not achieve the requested coverage — the words it missed are still uncovered, so the waveform base will be visible there.${reason} ${covering}, rather than the requested "${requestedWords.join(' ')}".${gaps}`;
    }
}
